import { TopPageMiddleBannerCarousel } from '@/components/carousels/TopPageMiddleBannerCarousel';
import { Link } from '@/components/link/Link';
import { TopPageFooterBannerList } from '@/components/lists/TopPageFooterBannerList';
import { Loading } from '@/components/loading/Loading';
import { TopPageBannerPresenter } from '@/domain/presenter/topPageBannerPresenter';
import { TopPageBannersState } from '@/domain/state/bannerState';
import {
  getActiveOnlineStatuses,
  getActiveRooms,
  getAllRankings,
  getAvailableCouponsByUserId,
  getCategories,
  getCouponCodeByCode,
  getFortuneMethods,
  getFutureSchedules,
  getRecentHighlightReviews,
  getListedFeatures,
  getPlansInIds,
  getRecommendedTellers,
  getTellersInIds,
  getViews,
} from '@raysee/firestore';
import { useAuth, useRouter, useWindowDimensions } from '@raysee/hooks';
import { DiagonalIcon } from '@raysee/icon';
import {
  OnlineStatus,
  PlanRankingBySatisfaction,
  QUESTIONS,
  RankingType,
  Schedule,
  TellerRankingByCategory,
  TellerRankingByFortuneMethod,
  TellerRankingBySatisfaction,
  isDisabledTeller,
  isTodaySchedule,
} from '@raysee/schema';
import { flatten } from '@raysee/utility';
import { InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import Script from 'next/script';
import useSWR from 'swr';
import {
  CategoryRankingCarousel,
  Container,
  FeatureCarousel,
  Footer,
  FortuneMethodRankingCarousel,
  Header,
  Main,
  MoreButton,
  PlanRankingList,
  QuestionList,
  ReviewList,
  Section,
  TabMenu,
  TellerCarousel,
  TellerGridList,
  TellerRankingList,
  TellerStatus,
  TopPageRectangleBannerCarousel,
  getTellerStatus,
  withLayout,
} from '../components';
import { DisplayType } from '../contexts';
import { usePlanModalContext } from '../features/purchase/usePlanModalContext';
import { Article } from '../features/wordpress/Article';
import { FaqArticle } from '../features/wordpress/FaqArticle';
import {
  useCurrentUser,
  useDisplayTypeContext,
  useRecentHighlightReviews,
  usePlans,
  useTellers,
} from '../hooks';
import {
  CategoryRankingViewModel,
  FortuneMethodRankingViewModel,
  TellerViewModel,
} from '../viewModels';
import { useState } from 'react';

const topPageBannerPresenter = new TopPageBannerPresenter();
type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const REVIEWS_PER_PAGE = 10;
const REVIEWS_MAX_CAP = REVIEWS_PER_PAGE * 3;

export const getServerSideProps = async ({ query }) => {
  // 動的インポートでfirebase-adminをサーバーサイドのみで読み込む
  const { setupApp } = await import('../server/setupApp');
  setupApp();
  const [
    recommendedTellers,
    reviews,
    rankings,
    schedules,
    rooms,
    categories,
    fortuneMethods,
    views,
    features,
    couponCodes,
    onlineStatuses,
  ] = await Promise.all([
    getRecommendedTellers(),
    getRecentHighlightReviews({ limitNum: REVIEWS_PER_PAGE }),
    getAllRankings(),
    getFutureSchedules(),
    getActiveRooms(),
    getCategories(),
    getFortuneMethods(),
    getViews(),
    getListedFeatures(),
    getCouponCodeByCode(query.code),
    getActiveOnlineStatuses(),
  ]);
  const planIds = Array.from(
    new Set([
      ...rankings
        .map((ranking) => (ranking.type === RankingType.PlansBySatisfaction ? ranking.planIds : []))
        .reduce(flatten, []),
      ...features.map((feature) => feature.rankingItemIds).reduce(flatten, []),
    ]),
  );
  const plans = await getPlansInIds(planIds);

  const recommendedTellerIds = recommendedTellers.map((teller) => teller.id);
  const tellerIds = Array.from(
    new Set([
      ...reviews.map((review) => review.tellerId),
      ...rankings
        .map((ranking) =>
          ranking.type !== RankingType.PlansBySatisfaction ? ranking.tellerIds : [],
        )
        .reduce(flatten, []),
      ...schedules.map((schedule) => schedule.tellerId),
      ...plans.map((plan) => plan.tellerId),
      ...onlineStatuses.map((onlineStatus) => onlineStatus.tellerId),
    ]),
  ).filter((tellerId) => !recommendedTellerIds.includes(tellerId));
  const tellers = await getTellersInIds(tellerIds).then((tellers) => [
    ...tellers,
    ...recommendedTellers,
  ]);
  const title =
    query.type === 'phone'
      ? '電話占い | RAYSEE(レイシー) 【初回10分無料で当たると話題】'
      : 'チャット占い | RAYSEE(レイシー) 【初回無料で当たると話題】';
  return {
    props: {
      tellers,
      plans,
      reviews,
      rankings,
      schedules,
      rooms,
      categories,
      fortuneMethods,
      views,
      features,
      couponCode: couponCodes[0] ?? null,
      onlineStatuses,
      title,
    },
  };
};

const Page: NextPage<Props> = (props) => {
  const router = useRouter();
  const { width: windowWidth } = useWindowDimensions();
  const [auth, authLoading, error] = useAuth();
  const { user } = useCurrentUser();
  const { displayType } = useDisplayTypeContext();
  const tellers = useTellers(props);
  const { plans } = usePlans(props);

  const [reviewPage, setReviewPage] = useState(1);
  const onClickMoreButton = () => setReviewPage((previous) => previous + 1);
  const currentLimit = reviewPage * REVIEWS_PER_PAGE;

  const allReviewViewModels = useRecentHighlightReviews(props, {
    limitNum: REVIEWS_MAX_CAP,
  });
  const reviewViewModels = allReviewViewModels.slice(0, Math.min(currentLimit, REVIEWS_MAX_CAP));
  const isOverReviewsCap = reviewViewModels.length >= REVIEWS_MAX_CAP;

  const { renderPlanModal } = usePlanModalContext();
  const { data: topPageBannersState, isLoading: topPageBannersLoading } = useSWR(
    authLoading || error ? null : ['banners', displayType, auth?.uid],
    (): Promise<TopPageBannersState> =>
      auth?.uid
        ? topPageBannerPresenter.getTopPageBannersForAuthUser(auth.uid)
        : topPageBannerPresenter.getTopPageBannersForGuest(),
    {
      fallbackData: {
        topPagePhoneRectangleBanners: [],
        topPageChatRectangleBanners: [],
        topPageMiddleBanners: [],
        topPageFooterBanners: [],
      } as TopPageBannersState,
      revalidateOnMount: true,
      revalidateOnFocus: true,
      loadingTimeout: 1000,
    },
  );
  const { data: rankings } = useSWR('rankings', () => getAllRankings(), {
    fallbackData: props.rankings,
  });
  const { data: schedules } = useSWR('schedules/future', () => getFutureSchedules(), {
    fallbackData: props.schedules,
  });
  const { data: categories } = useSWR('categories', () => getCategories(), {
    fallbackData: props.categories,
  });
  const { data: fortuneMethods } = useSWR('fortuneMethods', () => getFortuneMethods(), {
    fallbackData: props.fortuneMethods,
  });
  const { data: coupons } = useSWR(
    'coupons/available',
    () => (user ? getAvailableCouponsByUserId(user.id) : []),
    {
      revalidateOnMount: true,
      revalidateOnFocus: true,
    },
  );
  const { data: features } = useSWR('features', () => getListedFeatures(), {
    fallbackData: props.features,
  });
  const { data: onlineStatuses } = useSWR('onlineStatuses', () => getActiveOnlineStatuses(), {
    fallbackData: props.onlineStatuses,
  });

  const todayTellers = tellers
    .filter(filterTodayTeller(displayType, schedules, onlineStatuses))
    .map((teller) => ({
      teller,
      priority: getPriority(teller, displayType),
    }))
    .sort((a, b) => b.priority - a.priority)
    .map(({ teller }) => teller);

  const recommendedTellersData = tellers
    .filter((teller) => teller.recommendedOrder)
    .sort((a, b) => (a.recommendedOrder > b.recommendedOrder ? 1 : -1));

  const tellersRankingByPhoneCategory = rankings
    .filter((ranking) => ranking.type === RankingType.PhoneTellersByCategory)
    .map((ranking: TellerRankingByCategory) => ({
      ranking: new CategoryRankingViewModel({
        ranking,
        category: categories.find((category) => category.id === ranking.categoryId),
      }),
      tellers: ranking.tellerIds
        .map((tellerId) => tellers.find((teller) => teller.id === tellerId))
        .filter((teller) => !!teller),
    }));

  const tellersRankingByPhoneFortuneMethod = rankings
    .filter((ranking) => ranking.type === RankingType.PhoneTellersByFortuneMethod)
    .map((ranking: TellerRankingByFortuneMethod) => ({
      ranking: new FortuneMethodRankingViewModel({
        ranking,
        fortuneMethod: fortuneMethods.find(
          (fortueMethod) => fortueMethod.id === ranking.fortuneMethodId,
        ),
      }),
      tellers: ranking.tellerIds
        .map((tellerId) => tellers.find((teller) => teller.id === tellerId))
        .filter((teller) => !!teller && !isDisabledTeller(teller)),
    }));

  const tellersRankingByChatCategory = rankings
    .filter((ranking) => ranking.type === RankingType.ChatTellersByCategory)
    .map((ranking: TellerRankingByCategory) => ({
      ranking: new CategoryRankingViewModel({
        ranking,
        category: categories.find((category) => category.id === ranking.categoryId),
      }),
      tellers: ranking.tellerIds
        .map((tellerId) => tellers.find((teller) => teller.id === tellerId))
        .filter((teller) => !!teller && !isDisabledTeller(teller)),
    }));

  const tellersRankingByChatFortuneMethod = rankings
    .filter((ranking) => ranking.type === RankingType.ChatTellersByFortuneMethod)
    .map((ranking: TellerRankingByFortuneMethod) => ({
      ranking: new FortuneMethodRankingViewModel({
        ranking,
        fortuneMethod: fortuneMethods.find(
          (fortueMethod) => fortueMethod.id === ranking.fortuneMethodId,
        ),
      }),
      tellers: ranking.tellerIds
        .map((tellerId) => tellers.find((teller) => teller.id === tellerId))
        .filter((teller) => !!teller && !isDisabledTeller(teller)),
    }));

  const tellersRankingBySatisfaction = (
    rankings.find(
      (ranking) => ranking.type === RankingType.TellersBySatisfaction,
    ) as TellerRankingBySatisfaction
  )?.tellerIds
    .map((tellerId) => tellers.find((teller) => teller.id === tellerId))
    .filter((teller) => !!teller && !isDisabledTeller(teller));

  const plansRankingBySatisfaction = (
    rankings.find(
      (ranking) => ranking.type === RankingType.PlansBySatisfaction,
    ) as PlanRankingBySatisfaction
  )?.planIds
    .map((planId) => plans.find((plan) => plan.id === planId))
    .map((plan) => ({
      plan,
      teller: tellers.find((teller) => teller.id === plan?.tellerId && !isDisabledTeller(teller)),
    }));

  const featureCarousels = features.map((feature) => (
    <Section key={feature.id} title={feature.title}>
      <FeatureCarousel
        featureId={feature.id}
        style={feature.style}
        plans={feature
          .rankingItemIds!.map((itemId) => {
            const plan = plans.find((plan) => plan.id === itemId);
            const teller = tellers.find((teller) => teller.id === plan?.tellerId);
            return {
              plan,
              teller,
            };
          })
          .filter(({ plan, teller }) => !!plan && !!teller)}
        onClick={(teller, plan) =>
          renderPlanModal({
            teller,
            plan,
            skipDetail: false,
            searchParams: new URLSearchParams('from=chattop_plan_highlight_secret_ranking'),
          })
        }
      />
    </Section>
  ));

  return withLayout(
    <Container>
      <Script id="google-analytics-page-view">
        {`
          gtag('event', 'page_view', {
            'send_to': 'AW-10906938622',
            'user_id': '${auth?.uid ?? ''}',
          });
        `}
      </Script>
      <Script id="google-analytics-conversion-top">
        {`
          gtag('event', 'conversion', {'send_to': 'AW-10906938622/5sxXCImGlcUYEP7Z6tAo'});
        `}
      </Script>
      <Header />
      {!!props.couponCode?.imageUrl && (
        <NextSeo
          openGraph={{
            images: [
              {
                url: props.couponCode.imageUrl,
                width: 692,
                height: 363,
                alt: 'クーポンをゲット！',
              },
            ],
          }}
          title={props.title}
        />
      )}
      {displayType === 'phone' && (
        <NextSeo
          title="電話占い | RAYSEE(レイシー) 【初回10分無料で当たると話題】"
          canonical={`${window.location.origin}/?type=phone`}
        />
      )}
      <Main>
        <section className="bg-fuchsia-50 pb-8 pt-4 min-h-[80vw] xs:min-h-[500px]">
          {topPageBannersLoading ? (
            <div style={{ height: '500px' }}>
              <Loading size={12} color="border-t-rose-400" />
            </div>
          ) : (
            <TopPageRectangleBannerCarousel
              bannerStates={
                displayType === 'phone'
                  ? topPageBannersState.topPagePhoneRectangleBanners
                  : topPageBannersState.topPageChatRectangleBanners
              }
              from={`${displayType === 'phone' ? 'phone' : 'chat'}top_features_cardcarousel`}
              userId={auth?.uid}
              height={830}
              width={580}
            />
          )}
        </section>
        {user && (
          <section className="p-4">
            <div className="flex items-center justify-between rounded-3xl border-2 border-inherit bg-white px-4 py-3">
              <p className="truncate text-sm font-bold text-gray-600">
                {user.name ?? 'ゲスト'}さん
              </p>
              {coupons?.length > 0 && (
                <div className="flex shrink-0">
                  <DiagonalIcon height={24} width={19} />
                  <div>
                    <p className="text-xxs -mt-2 text-[#CACACA]">保有クーポン</p>
                    <p className="-mb-2 text-right text-sm font-bold text-gray-600">
                      {new Intl.NumberFormat('ja-JP').format(
                        coupons.reduce((all, coupon) => all + coupon.price, 0),
                      )}
                      円分
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
        {todayTellers.length > 0 && (
          <section>
            <div className="mx-4 flex items-center justify-between pt-4">
              <h1 className="border-l-8 border-rose-400 pl-2 text-lg font-bold text-rose-400">
                {displayType === 'phone' ? '今日占える占い師' : 'オンラインの占い師'}
              </h1>
            </div>
            <div className="pb-3 pt-1">
              <TellerCarousel data={todayTellers} displayType={displayType} />
            </div>
          </section>
        )}
        {topPageBannersState.topPageMiddleBanners.length > 0 && (
          <section className="mb-8 mt-1">
            <TopPageMiddleBannerCarousel
              banners={topPageBannersState.topPageMiddleBanners}
              width={windowWidth}
              height={(windowWidth / 350) * 80}
              centerPadding="0"
              gap={16}
              displayType={displayType}
            />
          </section>
        )}
        {displayType === 'chat' && <>{featureCarousels}</>}
        <Section title={displayType === 'phone' ? 'おすすめ電話占い師' : 'おすすめチャット占い師'}>
          <TellerGridList
            data={
              displayType === 'phone'
                ? recommendedTellersData
                    .filter((slot) => slot.minPrice.phone)
                    .filter(
                      (slot) => slot.recommendedPlanType === 'phone' || !slot.recommendedPlanType,
                    )
                : recommendedTellersData
                    .filter((slot) => slot.minPrice.chat)
                    .filter(
                      (slot) =>
                        slot.recommendedPlanType === 'chat_once' || !slot.recommendedPlanType,
                    )
            }
            displayType={displayType}
          />
        </Section>
        <section className="my-4 py-4">
          <h1 className="mx-4 border-l-8 border-rose-400 pl-2 text-lg font-bold text-rose-400">
            相談内容別 占い師ランキング
          </h1>
          <CategoryRankingCarousel
            data={
              displayType === 'phone' ? tellersRankingByPhoneCategory : tellersRankingByChatCategory
            }
            displayType={displayType}
          />
        </section>
        <section className="my-4 py-4">
          <h1 className="mx-4 border-l-8 border-rose-400 pl-2 text-lg font-bold text-rose-400">
            人気占術別 占い師ランキング
          </h1>
          <FortuneMethodRankingCarousel
            data={
              displayType === 'phone'
                ? tellersRankingByPhoneFortuneMethod
                : tellersRankingByChatFortuneMethod
            }
            displayType={displayType}
          />
        </section>
        {displayType === 'phone' && (
          <Section title="占い師 満足度ランキング">
            <TellerRankingList
              num={5}
              data={tellersRankingBySatisfaction}
              displayType={displayType}
              style="none"
              from={`${displayType === 'phone' ? 'phone' : 'chat'}top_tellers_satisfaction_ranking`}
              showRankingCrown
            />
            <div className="mt-3 flex justify-center">
              <Link
                href={`/rankings/${RankingType.TellersBySatisfaction}?from=phonetop_tellers_satisfaction_ranking`}
                legacyBehavior
              >
                <div className="flex h-[35px] w-[130px] items-center justify-center rounded-full bg-[#F2F2F2]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 fill-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm">もっと見る</p>
                </div>
              </Link>
            </div>
          </Section>
        )}
        {displayType === 'chat' && (
          <Section title="鑑定メニュー 満足度ランキング">
            <PlanRankingList
              num={5}
              data={plansRankingBySatisfaction}
              displayType={displayType}
              style="ranking"
            />
            <div className="mt-3 flex justify-center">
              <Link
                href={`/rankings/${RankingType.PlansBySatisfaction}?from=chattop_plans_satisfaction_ranking`}
                legacyBehavior
              >
                <div className="flex h-[35px] w-[130px] items-center justify-center rounded-full bg-[#F2F2F2]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 fill-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm">もっと見る</p>
                </div>
              </Link>
            </div>
          </Section>
        )}
        <Section title="リアル口コミ体験談">
          <ReviewList
            reviews={reviewViewModels}
            tellers={tellers}
            from={`${displayType === 'phone' ? 'phone' : 'chat'}top_teller_review_list`}
          />
          {!isOverReviewsCap ? (
            <div>
              <MoreButton
                component="button"
                onClick={onClickMoreButton}
              />
            </div>
          ) : (
            <div>
              <MoreButton
                component="a"
                href={`/${displayType === 'phone' ? 'phone' : ''}reviews?from=${
                displayType === 'phone' ? 'phone' : 'chat'
                }top_teller_review_list`}
              />
            </div>
          )}
   
        </Section>
        <section className="my-4 py-4">
          <h1 className="mx-4 border-l-8 border-rose-400 pl-2 text-lg font-bold text-rose-400 mb-5">
            カテゴリー別 最新記事
          </h1>
          <Article />
        </section>
        <Section title="よくある質問" id="FAQ">
          <QuestionList
            questions={QUESTIONS.filter((question) => question.displayType === displayType)}
          />
          <p className="text-center">その他質問がある場合</p>
          <FaqArticle />
        </Section>
        {topPageBannersState.topPageFooterBanners.length > 0 && (
          <section className="flex flex-col items-center bg-gray-500 pt-8">
            <TopPageFooterBannerList banners={topPageBannersState.topPageFooterBanners} />
          </section>
        )}
        <Footer />
      </Main>
      <TabMenu selectedMenu="home" />
    </Container>,
    false,
    undefined,
    router.query,
    coupons,
  );
};

export default Page;

const HIGHEST_PRIORITY_TELLER_IDS = [
  'VqLQEdh5WYMLjcqg2rb51mDaEZn2',
  'eLurz1UhzlgXhWuKELO5TPwz1xt2',
  'qEIWFISVV2b2t1oaNDr4ruTaQiR2',
];

const filterTodayTeller =
  (displayType: DisplayType, schedules: Schedule[], onlineStatuses: OnlineStatus[]) =>
  (teller: TellerViewModel) =>
    displayType === 'phone'
      ? !!teller.minPrice?.phone &&
        (schedules.some(
          (schedule) => schedule.tellerId === teller.id && isTodaySchedule(schedule),
        ) ||
          ['progress', 'standby'].includes(teller.dummyStatus))
      : !!teller.minPrice?.chat &&
        (HIGHEST_PRIORITY_TELLER_IDS.includes(teller.id) ||
          onlineStatuses.some((onlineStatus) => onlineStatus.tellerId === teller.id) ||
          ['progress', 'standby'].includes(teller.dummyStatus));

// TODO: 占い師IDがハードコーディングされてるので、定数化したい
const getPriority = (teller: TellerViewModel, displayType: DisplayType) => {
  if (displayType === 'chat') {
    return teller.id === 'SpjjQYCh9pdbAxJoZWNggaKxVPz1'
      ? 100
      : teller.id === 'o1CH2bzMOYdihEiJEHrU1ucSzJC3'
        ? 99
        : teller.id === 'wQUnnw5BI4XtYGvdNGSpnJNKe0F3'
          ? 98
          : teller.id === 'B6QP4VDeQYRqpdsfCoGf73MtCAw2'
            ? 97
            : HIGHEST_PRIORITY_TELLER_IDS.includes(teller.id)
              ? 96
              : ['pro', 'staff'].includes(teller.role)
                ? 95
                : ['progress', 'standby'].includes(teller.dummyStatus)
                  ? 94
                  : 0;
  } else {
    const status = getTellerStatus(teller, teller.schedules, teller.rooms);
    switch (status) {
      case TellerStatus.Standby: {
        return teller.role === 'pro' ? 51 : teller.role === 'staff' ? 49 : 50;
      }
      case TellerStatus.Break: {
        return teller.role === 'pro' ? 41 : teller.role === 'staff' ? 39 : 40;
      }
      case TellerStatus.Available: {
        return teller.role === 'pro' ? 31 : teller.role === 'staff' ? 29 : 30;
      }
      case TellerStatus.Calling: {
        return teller.role === 'pro' ? 11 : teller.role === 'staff' ? 9 : 10;
      }
      case TellerStatus.Unavailable: {
        return teller.role === 'pro' ? 2 : teller.role === 'staff' ? 0 : 1;
      }
    }
  }
};
